<script lang="ts" setup>
import VPageContainer from '@shared/layout/VPageContainer/VPageContainer.vue';
import VDataTable from '@shared/component/VDataTable/VDataTable.vue';
import VColumn from '@shared/component/VDataTable/VColumn.vue';
import VStatusTag from '@shared/component/VStatusTag/VStatusTag.vue';
import { FontAwesomeIcon } from '@shared/plugin/icons';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import { useRowLinkNavigation } from '@shared/component/VDataTable/useRowLinkNavigation';
import { useMutation } from '@tanstack/vue-query';
import { VButton } from '@shared/component/VButton';
import { computed, ref, useTemplateRef, watch } from 'vue';
import type { BasicColor } from '@tailwind-preset/colors';
import VSearch from '@shared/component/VSearch/VSearch.vue';
import VModal from '@shared/component/VModal/VModal.vue';
import { useClaims } from '@auth/useClaims';
import VSelect from '@shared/form/VSelect/VSelect.vue';
import { useTussenpersonenGet } from '@services/crm-internal/orval-api/tussenpersonen-api/tussenpersonen-api';
import { useRouteQuery } from '@vueuse/router';
import { createErrorMessage } from '@errors/helpers';
import VInfoBox from '@shared/component/VInfoBox/VInfoBox.vue';
import { useNotification } from '@shared/plugin/prime-vue/useNotification';
import { useWatchErrorHandler } from '@errors/useErrorHandler';
import { ModalType } from '@shared/component/types';
import apiService from '@services/base/apiService';
import { useBreadcrumb } from '@menu-lib/breadcrumb/breadcrumb';
import VButtonGroup from '@shared/component/VButtonGroup/VButtonGroup.vue';
import NieuweRelatieButton from './NieuweRelatieButton.vue';
import UitgebreidZoekenModal from './UitgebreidZoekenModal.vue';
import type { SearchParams } from './types';
import { useRelatiesOverzicht } from './relaties-overzicht';

const notification = useNotification();

const limit = ref<number>(20);
const page = ref<number>(0);
const naamNummer = ref<string>('');
const deleteModalOpen = ref<boolean>(false);
const selectedRelatienummer = ref<number>();
const offset = computed(() => page.value * limit.value);
const adviseur = ref<{ label: string; value: number | null }>({
  label: 'Alle adviseurs',
  value: null,
});

const isSearchModalOpen = ref<boolean>(false);
const searchParams = ref<SearchParams>({});

const claims = useClaims();
const isMuterenEnRoyeren = useRouteQuery('muteren', undefined, {
  transform: (value) => value === 'true',
});

const showAdviseur = computed(
  () => adviseur.value?.value === null && claims?.value.isInternal === true,
);

const title = computed(() => (isMuterenEnRoyeren.value ? 'Muteren / Royeren' : 'Relaties'));

useBreadcrumb([{ link: '', name: title.value }]);

const uitgebreidZoeken = useTemplateRef('uitgebreidZoeken');

const statusTagColors: Record<string, BasicColor> = {
  Prospect: 'grey',
  Actief: 'green',
  Beëindigd: 'red',
  'Niet acceptabel': 'black',
};

const {
  relaties: relatiesData,
  totalItems,
  isFetching,
  isLoading,
  isError,
  error,
  isUitgebreidZoeken,
  uitgebreidViaManagement,
  invalideerCache,
} = useRelatiesOverzicht({
  naamNummer,
  limit,
  offset,
  isMuterenEnRoyeren,
  searchParams,
  adviseurId: computed(() => adviseur.value?.value),
});

const { data: adviseurs, isPending: adviseursPending } = useTussenpersonenGet(undefined, {
  query: {
    select: (res) => res.result,
    enabled: computed(() => claims?.value?.isInternal ?? false),
  },
});
const adviseurOptions = computed(() => [
  { label: 'Alle adviseurs', value: null },
  ...(adviseurs.value?.map((a) => ({ label: `${a.id} - ${a.name}`, value: a.id })) ?? []),
]);

const relaties = computed(() =>
  isLoading.value
    ? Array.from({ length: relatiesData.value.length || limit.value })
    : relatiesData.value,
);

const {
  mutate: deleteRelation,
  error: deleteError,
  isPending: isDeleting,
} = useMutation({
  mutationFn: () =>
    apiService.apiClient.post(
      `/net/relation/deleterelation?relatienr=${selectedRelatienummer.value}`,
    ),
  onSuccess: (res) => {
    if (res.data === 'OK') {
      notification.success('Relatie is succesvol verwijderd.');
      deleteModalOpen.value = false;
      invalideerCache();
    } else {
      notification.error(res.data);
    }
  },
});
useWatchErrorHandler(deleteError);

const uitgebreidZoekenFiltersActief = computed(
  () => Object.values(searchParams.value).filter((k) => k.length > 0).length,
);

const { onRowClicked } = useRowLinkNavigation({
  rowLink: (row: any) => ({
    name: isMuterenEnRoyeren.value ? 'polissen' : 'algemeen',
    params: { relatienummer: row.RelatieNummer },
  }),
});

function openDeleteModal(relatienummer: number) {
  deleteModalOpen.value = true;
  selectedRelatienummer.value = relatienummer;
}

function onPageChange(event: { page: number }) {
  page.value = event.page;
}

function resetUitgebreidZoeken() {
  searchParams.value = {};
  uitgebreidZoeken.value?.reset();
}

const dataTable = useTemplateRef('relatietabel');

// De toggle kan mid-sessie omgaan (App Config-flip); de twee uitgebreid-zoeken-backends hebben
// verschillende resultaatsets, dus een bewaarde offset kan dan voorbij het einde wijzen.
// undefined telt als false: geen echte wissel.
watch(uitgebreidViaManagement, (nieuw, oud) => {
  if ((nieuw === true) === (oud === true)) {
    return;
  }
  if (!isUitgebreidZoeken.value) {
    return;
  }
  page.value = 0;
  dataTable.value?.resetPagination();
});

function onSearchInput(searchTerm: string) {
  naamNummer.value = searchTerm;
  page.value = 0;
  dataTable.value?.resetPagination();
}
</script>

<template>
  <VPageContainer :title>
    <template #actions>
      <div class="flex flex-col gap-4 md:flex-row">
        <VButtonGroup attached>
          <VButton
            prepend-icon="far fa-landmark-magnifying-glass"
            inverted
            @click="isSearchModalOpen = true"
          >
            Uitgebreid zoeken
            {{ uitgebreidZoekenFiltersActief > 0 ? `(${uitgebreidZoekenFiltersActief})` : '' }}
          </VButton>
          <VButton
            @click="resetUitgebreidZoeken()"
            inverted
            type="danger"
            v-if="uitgebreidZoekenFiltersActief > 0"
            append-icon="far fa-xmark"
          />
        </VButtonGroup>

        <NieuweRelatieButton v-if="!isMuterenEnRoyeren" />
      </div>
    </template>

    <div class="flex flex-col gap-4">
      <div class="flex grow flex-wrap gap-4 md:flex-nowrap">
        <VSearch class="grow" @input="onSearchInput" />
        <!-- @vue-skip onClear kan niet gevonden worden omdat eze alleen bestaat indien vselect searchable is.-->
        <template v-if="claims?.isInternal">
          <VSkeleton class="max-w-72" v-if="adviseursPending" height="lg" />
          <VSelect
            v-else
            searchable
            :options="adviseurOptions"
            option-label="label"
            v-model="adviseur"
            @clear="adviseur = { label: 'Alle adviseurs', value: null }"
            placeholder="Adviseur"
          />
        </template>
      </div>

      <VInfoBox badge-text="error" type="danger" v-if="isError">
        <div v-html="createErrorMessage(error)"></div>
      </VInfoBox>

      <VDataTable
        v-else
        :key="String(isUitgebreidZoeken)"
        :rows="limit"
        @[!isFetching&&`row-click`]="onRowClicked"
        :show-row-arrow="!isFetching"
        :value="relaties"
        :total-records="totalItems"
        lazy
        @page="onPageChange"
        ref="relatietabel"
      >
        <VColumn v-if="isFetching" field="loading">
          <template #body>
            <div class="flex w-full gap-5">
              <VSkeleton class="basis-10" :timeout="0" />
              <VSkeleton class="basis-64" :timeout="0" />
              <VSkeleton class="basis-64" :timeout="0" />
              <VSkeleton class="basis-48" :timeout="0" />
              <VSkeleton class="basis-48" :timeout="0" />
              <VSkeleton class="basis-48" :timeout="0" />
            </div>
          </template>
        </VColumn>

        <template v-else>
          <VColumn field="TypeRelatie" header="">
            <template #body="{ data }">
              <div
                class="flex size-5 items-center justify-center rounded text-xs font-bold text-white"
                :class="{
                  'bg-blue-700': data.TypeRelatie === 'Z',
                  'bg-green-700': data.TypeRelatie === 'P',
                }"
              >
                {{ data.TypeRelatie }}
              </div>
            </template>
          </VColumn>
          <VColumn field="RelatieNaam" header="Relatie">
            <template #body="{ data }">
              <div class="flex flex-wrap items-baseline gap-x-2">
                {{ data.RelatieNaam }}
                <div class="text-sm text-grey">
                  {{ data.RelatieNummer }}
                </div>
              </div>
            </template>
          </VColumn>
          <VColumn field="Adviseurnummer">
            <template #header>
              <span class="text-sm font-bold text-grey-500">
                {{ showAdviseur ? 'Adviseur' : '' }}
              </span>
            </template>
            <template #body="{ data }">
              <!-- tijdelijke fix voor bug in primevue, de kolom verspringt als we v-if gebruiken in VColumn -->
              <div class="flex flex-wrap items-baseline gap-x-2" v-if="showAdviseur">
                {{ data.Adviseurnaam }}
                <div class="text-sm text-grey">
                  {{ data.Adviseurnummer }}
                </div>
              </div>
            </template>
          </VColumn>
          <VColumn field="Adres" header="Adres" />
          <VColumn field="Plaats" header="Plaats" />
          <VColumn field="StatusNaam" header="" v-if="!isMuterenEnRoyeren">
            <template #body="{ data }">
              <VStatusTag inverted :color="statusTagColors[data.StatusNaam]">{{
                data.StatusNaam
              }}</VStatusTag>
            </template>
          </VColumn>
          <VColumn v-if="!isMuterenEnRoyeren" field="" header="">
            <template #body="{ data }">
              <button
                v-if="data.StatusNaam === 'Prospect'"
                aria-label="Relatie verwijderen"
                class="size-full text-center text-primary-900 transition-colors hover:text-red"
                type="button"
                @click.stop="openDeleteModal(data.RelatieNummer)"
              >
                <FontAwesomeIcon icon="far fa-trash" />
              </button>
            </template>
          </VColumn>
        </template>
      </VDataTable>
    </div>

    <VModal
      invert-header
      :type="ModalType.Danger"
      title="Relatie verwijderen"
      confirm-button-text="Verwijderen"
      @confirm-action="deleteRelation()"
      @close-modal="deleteModalOpen = false"
      :is-active="deleteModalOpen"
      :loading="isDeleting"
    >
      Weet je zeker dat je de relatie '{{
        relatiesData.find((x) => x.RelatieNummer === selectedRelatienummer)?.RelatieNaam
      }}' wilt verwijderen?
    </VModal>
    <UitgebreidZoekenModal
      ref="uitgebreidZoeken"
      v-model:is-open="isSearchModalOpen"
      @search="(sp) => (searchParams = sp)"
    />
  </VPageContainer>
</template>
